import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  selectCurrentUser,
  selectCurrentToken,
  selectHasRole, 
  selectHasAnyRole, 
  selectHasPermission,
  selectIsAdmin
} from '../../features/auth/authSlice';
import { isTokenExpired } from '../../utils/tokenUtils';
import LoadingSpinner from '../ui/LoadingSpinner';

function ProtectedRoute({ 
  children, 
  requiredRole = null, 
  requiredRoles = [], 
  requiredPermission = null,
  adminOnly = false,
  redirectTo = '/login'
}) {
  const location = useLocation();
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const isAuthenticated = !!user && !!token;
  const isLoading = false; 
  
  // Check if token is expired
  const tokenExpired = token ? isTokenExpired(token) : true;
  
  // Get all required auth state at once to avoid conditional hooks
  const isAdmin = useSelector(selectIsAdmin);
  const hasRole = useSelector(state => requiredRole ? selectHasRole(state, requiredRole) : true);
  const hasAnyRole = useSelector(state => requiredRoles.length > 0 ? selectHasAnyRole(state, requiredRoles) : true);
  const hasPermission = useSelector(state => requiredPermission ? selectHasPermission(state, requiredPermission) : true);
  
  
  
  // If still loading, show loading spinner
  if (isLoading) {
    return <LoadingSpinner fullPage />;
  }
  
  // Check authentication first, including token validity
  if (!isAuthenticated || tokenExpired) {
    if (tokenExpired) {
      // Console log removed for production
    } else {
      // Console log removed for production
    }
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check admin access
  if (adminOnly && !isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check specific role
  if (requiredRole && !hasRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check any of multiple roles
  if (requiredRoles.length > 0 && !hasAnyRole) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check specific permission
  if (requiredPermission && !hasPermission) {
    return <Navigate to="/unauthorized" replace />;
  }

  // If authenticated and has required roles/permissions, render children
  // Console log removed for production
  return children;
}

// Specific role-based route components
export const AdminRoute = ({ children }) => (
  <ProtectedRoute adminOnly={true}>
    {children}
  </ProtectedRoute>
);

export const ClaimSubmitterRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={['HR', 'Broker', 'CRM']}>
    {children}
  </ProtectedRoute>
);

export const ClaimProcessorRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={['Claims_Officer', 'Approver']}>
    {children}
  </ProtectedRoute>
);

export const ApproverRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={['Approver']}>
    {children}
  </ProtectedRoute>
);

export const FinanceRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={['Finance']}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
